import { defineStore } from 'pinia';
import {
    getDefaultSubscriptionConfig,
    createTierPriorityMap,
    SUBSCRIPTION_CAPABILITIES,
    SUBSCRIPTION_QUOTAS,
    FALLBACK_TIER_ID
} from '@/config/subscriptionTiers';
import paymentService from '@/core/api/paymentService';

// PAY-07：状态同步请求去重 + 陈旧响应保护（模块级，非响应式）。
// - inFlightSync：复用同一个 in-flight Promise，避免并发触发重复请求与竞态覆盖。
// - syncSeq：单调请求序号；仅最新一次请求的结果写回 Store，较早发起但较晚返回的旧响应丢弃。
let inFlightSync = null;
let syncSeq = 0;

// 将一次成功的 status 响应写回 Store（抽离以配合 PAY-07 的陈旧响应保护分支）。
const applySubscriptionStatus = (store, status) => {
    const tierId = store.resolveTierId(
        status?.entitlement?.stripe?.price_lookup_key,
        status?.entitlement?.tier
    );

    store.userState = status?.user_state || store.userState;
    store.entitlement = status?.entitlement || null;
    store.credits = status?.credits || null;
    store.currentCycle = status?.current_cycle || null;
    store.creditQuota = normalizeCreditQuota(status?.credit_quota);
    store.lastSyncedAt = new Date().toISOString();

    // v2.1.13：持久化后端 payment status 事实字段（防御性归一化，缺失/非 boolean 视为 false）。
    // creditConsumptionExists 用三态（true/false/null），null 表示本次响应未拿到合法值（不应发生在 200），
    // 提醒评估以 === false 严格判断，避免 null 误触发。
    store.initialTrialCreditsGranted = status?.initial_trial_credits_granted === true;
    store.creditConsumptionExists =
        status?.credit_consumption_exists === true
            ? true
            : status?.credit_consumption_exists === false
                ? false
                : null;
    store.lazyOps = status?.lazy_ops || null;

    if (tierId) {
        store.setTier(tierId);
    } else if (store.userState !== 'paid') {
        store.setTier(FALLBACK_TIER_ID);
    }
};

/**
 * 将后端 v2.1.8 `GET /payments/status/` 的 `credit_quota` 归一化为前端模型。
 *
 * 规则（来自方案 4.3）：
 * - `credit_quota` / `credit_quota.trial` 在成功响应中必须存在，缺失视为 contract 异常。
 * - `credit_quota.cycle` 可为 object（有 active 月度 cycle）或 null（无 active 月度 cycle，非异常）。
 * - 数字字段只接受有限数；非法值视为 contract 异常（contractInvalid=true）。
 * - remaining 可大于 granted（积分滚动/累积场景，合法）。
 * - 异常时绝不按 `credits` 反推 quota。
 */
const normalizeCreditQuota = (creditQuota) => {
    if (!creditQuota || typeof creditQuota !== 'object' || !creditQuota.trial) {
        return { contractInvalid: true, cycle: null, trial: null };
    }

    const trialRemaining = Number(creditQuota.trial.remaining);
    const trialGranted = Number(creditQuota.trial.granted);
    const trialInvalid =
        !Number.isFinite(trialRemaining) ||
        !Number.isFinite(trialGranted);

    if (trialInvalid) {
        return { contractInvalid: true, cycle: null, trial: null };
    }

    if (!creditQuota.cycle) {
        return {
            contractInvalid: false,
            cycle: null,
            trial: {
                remaining: trialRemaining,
                granted: trialGranted
            }
        };
    }

    const cycleRemaining = Number(creditQuota.cycle.remaining);
    const cycleGranted = Number(creditQuota.cycle.granted);
    const cycleInvalid =
        !Number.isFinite(cycleRemaining) ||
        !Number.isFinite(cycleGranted);

    if (cycleInvalid) {
        return { contractInvalid: true, cycle: null, trial: null };
    }

    return {
        contractInvalid: false,
        cycle: {
            remaining: cycleRemaining,
            granted: cycleGranted
        },
        trial: {
            remaining: trialRemaining,
            granted: trialGranted
        }
    };
};

export const useSubscriptionStore = defineStore('subscription', {
    state: () => {
        const initialConfig = getDefaultSubscriptionConfig() || {};
        return {
            currentTierId: initialConfig.defaultTier || FALLBACK_TIER_ID,
            tierOrder: Array.isArray(initialConfig.order) ? [...initialConfig.order] : [],
            tierPriority: createTierPriorityMap(initialConfig.order || []),
            userState: 'trial',
            entitlement: null,
            credits: null,
            currentCycle: null,
            creditQuota: null,
            lastSyncedAt: null,
            quotaUsage: {},
            // v2.1.13 新增：后端 payment status 事实字段
            initialTrialCreditsGranted: false,   // 本次 sync 是否首次发放（request-scoped）
            creditConsumptionExists: null,       // lifetime 是否存在消费记录（null=未知，未 sync 过）
            lazyOps: null                        // 原始 lazy_ops 透传（trial_granted/cycle_granted/cycle_top_up_delta）
        };
    },
    
    getters: {
        // 直接从 subscriptionTiers.js 读取配置，避免重复存储
        allTiers() {
            const config = getDefaultSubscriptionConfig();
            return config && config.tiers ? config.tiers : {};
        },
        
        currentTier(state) {
            const config = getDefaultSubscriptionConfig();
            const tiers = config && config.tiers ? config.tiers : {};
            return tiers[state.currentTierId] || tiers[FALLBACK_TIER_ID] || null;
        },
        
        currentTierLabel(_state) {
            const tier = this.currentTier;
            return tier ? tier.label : '';
        },
        
        currentCapabilities(_state) {
            const tier = this.currentTier;
            return tier ? tier.capabilities : [];
        }
    },
    
    actions: {
        // 设置用户选择的层级
        setTier(tierId) {
            const config = getDefaultSubscriptionConfig();
            const tiers = config && config.tiers ? config.tiers : {};
            if (!tiers[tierId]) {
                this.currentTierId = FALLBACK_TIER_ID;
            } else {
                this.currentTierId = tierId;
            }
        },
        
        // 权限检查
        hasCapability(capability) {
            if (!capability) return false;
            return Array.isArray(this.currentCapabilities) && 
                   this.currentCapabilities.includes(capability);
        },
        
        // 层级比较
        isTierAtLeast(requiredTierId) {
            if (!requiredTierId) return true;
            
            const priorityMap = this.tierPriority || {};
            const currentPriority = priorityMap[this.currentTierId];
            const requiredPriority = priorityMap[requiredTierId];
            
            if (typeof requiredPriority !== 'number') return true;
            if (typeof currentPriority !== 'number') return false;
            
            return currentPriority >= requiredPriority;
        },
        
        // 获取当前层级的配额定义
        getTierQuota(quotaKey) {
            if (!quotaKey) return null;
            const tier = this.currentTier;
            return tier && tier.quotas ? tier.quotas[quotaKey] : null;
        },
        
        // 检查是否超过配额定义（预检，不做真实计数）
        isQuotaExceeded(quotaKey) {
            const quota = this.getTierQuota(quotaKey);
            // null 表示无限制
            return quota !== null && quota <= 0;
        },
        
        // 重置为默认状态
        resetState() {
            const initialConfig = getDefaultSubscriptionConfig();
            if (initialConfig) {
                this.currentTierId = initialConfig.defaultTier || FALLBACK_TIER_ID;
                this.tierOrder = Array.isArray(initialConfig.order) ? [...initialConfig.order] : [];
                this.tierPriority = createTierPriorityMap(initialConfig.order || []);
            }
            this.userState = 'trial';
            this.entitlement = null;
            this.credits = null;
            this.currentCycle = null;
            this.creditQuota = null;
            this.lastSyncedAt = null;
            this.quotaUsage = {};
            this.initialTrialCreditsGranted = false;
            this.creditConsumptionExists = null;
            this.lazyOps = null;
        },

        checkQuota(quotaKey, amount = 1) {
            if (!quotaKey) {
                return { allowed: true, limit: null, used: 0, remaining: null };
            }

            const quota = this.getTierQuota(quotaKey);
            if (quota === null || typeof quota === 'undefined') {
                return { allowed: true, limit: quota ?? null, used: 0, remaining: null };
            }

            const normalizedAmount = Number.isFinite(amount) && amount > 0 ? amount : 1;
            const used = this.quotaUsage?.[quotaKey] || 0;
            const remaining = quota - used;
            const allowed = remaining >= normalizedAmount && quota > 0;

            return {
                allowed,
                limit: quota,
                used,
                remaining: Math.max(remaining, 0)
            };
        },

        consumeQuota(quotaKey, amount = 1) {
            const quotaInfo = this.checkQuota(quotaKey, amount);
            if (!quotaInfo.allowed) {
                return quotaInfo;
            }

            if (quotaInfo.limit === null || typeof quotaInfo.limit === 'undefined') {
                return quotaInfo;
            }

            const normalizedAmount = Number.isFinite(amount) && amount > 0 ? amount : 1;
            const nextUsed = (this.quotaUsage?.[quotaKey] || 0) + normalizedAmount;
            this.quotaUsage = {
                ...(this.quotaUsage || {}),
                [quotaKey]: nextUsed
            };

            return {
                ...quotaInfo,
                used: nextUsed,
                remaining: Math.max(quotaInfo.limit - nextUsed, 0)
            };
        },

        async syncStatus() {
            // PAY-07：并发去重 —— 仍在飞行的同步直接复用同一 Promise，避免重复请求与竞态覆盖。
            if (inFlightSync) {
                return inFlightSync;
            }
            const seq = ++syncSeq;
            const run = (async () => {
                console.info('[billing] status sync start', { seq });
                const status = await paymentService.getStatus();
                // PAY-07：陈旧响应保护 —— 若期间已有更新的同步发起（seq 非最大），丢弃本次结果，
                // 避免较早发起、较晚返回的旧响应覆盖新状态。
                if (seq !== syncSeq) {
                    console.info('[billing] status sync stale response skipped', { seq, latest: syncSeq });
                    return status;
                }
                applySubscriptionStatus(this, status);
                console.info('[billing] status sync done', {
                    seq,
                    userState: this.userState,
                    currentTierId: this.currentTierId,
                    entitlementKind: this.entitlement?.kind || null,
                    creditQuotaContractInvalid: this.creditQuota?.contractInvalid || false,
                    creditQuotaCycleGranted: this.creditQuota?.cycle ? this.creditQuota.cycle.granted : null,
                    creditQuotaCycleRemaining: this.creditQuota?.cycle ? this.creditQuota.cycle.remaining : null,
                    creditQuotaTrialGranted: this.creditQuota?.trial ? this.creditQuota.trial.granted : null,
                    creditQuotaTrialRemaining: this.creditQuota?.trial ? this.creditQuota.trial.remaining : null,
                    lastSyncedAt: this.lastSyncedAt
                });
                return status;
            })();
            inFlightSync = run;
            try {
                return await run;
            } finally {
                if (inFlightSync === run) {
                    inFlightSync = null;
                }
            }
        },

        resolveTierId(lookupKey, entitlementTier) {
            if (typeof entitlementTier === 'string' && entitlementTier.trim()) {
                const normalizedTier = entitlementTier.trim().toLowerCase();
                if (normalizedTier === 'standard') return 'standard';
                if (normalizedTier === 'pro') return 'pro';
                if (normalizedTier === 'free') return 'free';
            }
            if (!lookupKey) return null;
            if (lookupKey.startsWith('standard_')) return 'standard';
            if (lookupKey.startsWith('pro_')) return 'pro';
            return null;
        },

        isOneOffEntitlementActive() {
            return this.entitlement?.kind === 'one_off';
        }
    }
});

export const SUBSCRIPTION_ENUMS = {
    CAPABILITIES: SUBSCRIPTION_CAPABILITIES,
    QUOTAS: SUBSCRIPTION_QUOTAS
};
